import { ReactElement, useEffect } from 'react';
import {
  Alert,
  Button,
  ContextMenu,
  FullLoader,
  Loader,
  Pill,
} from '@sc/shared-ui';
import {
  SubscriptionAction,
  SubscriptionStatus,
  SubscriptionType,
  UserCompanyLimitation,
} from '@sc/graphql';
import {
  ArrowRightIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { useQuery } from '@apollo/client/react';
import { useNavigate } from '@tanstack/react-router';
import { format, subMonths } from 'date-fns';
import {
  ESSENTIALS_STEPS,
  planDisplayName,
  stepIndexForSubtotal,
} from '@sc/stripe/pricing';
import { useUser } from '../providers/users';
import { useUsage } from '../hooks/useUsage';
import { GQL_QUERY_LIST_PAYMENT_METHODS } from '../utility/queries';
import { useSubscriptionActions } from '../hooks/useSubscriptionActions';
import { BillingPanel } from '../components/BillingPanel';
import { UsageBars } from '../components/UsageBars';

const formatGbp = (value: number): string =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(value);

export const BillingSubscriptionCreditsTab = ({
  onUpgrade
}: {
  onUpgrade: () => void;
}): ReactElement => {
  const { user } = useUser();
  const { usage, usageState, refresh } = useUsage();
  const navigate = useNavigate();

  const subscription = user.company.subscription;
  const outstandingInvoice = user.company.outstandingInvoice;

  const isEnterprise = subscription?.type === SubscriptionType.enterprise;

  const isPaused = subscription?.status === SubscriptionStatus.paused;
  const isPastDue = !!outstandingInvoice?.subscriptionUuid;
  const isActive = !!subscription && !isPaused && !isPastDue;

  // Paused is a distinct, recoverable state — surface it rather than lumping it
  // in with the red "Inactive" used for past-due/lapsed plans.
  const planStatus = !subscription
    ? { text: 'Free trial', colour: '#da892b' }
    : isActive
      ? { text: 'Active', colour: '#3aa16e' }
      : isPaused
        ? { text: 'Paused', colour: '#b8c5d0' }
        : { text: 'Inactive', colour: '#cb3b3b' };

  const {
    manageOptions,
    confirmAction,
    isUpdating,
    hasMonthlyPlanCard,
    openAddCard,
    addCardModal,
  } = useSubscriptionActions();

  const { data: cardsData } = useQuery(GQL_QUERY_LIST_PAYMENT_METHODS);

  const missingCard = !!subscription && !!cardsData && !hasMonthlyPlanCard;

  const noUsage = user.company.limitations.includes(
    UserCompanyLimitation.cannotCreateQueryMessage
  );

  const planName = subscription
    ? planDisplayName(subscription.type)
    : 'Free trial';

  const renewsAt = subscription?.renewsAt
    ? new Date(subscription.renewsAt)
    : null;

  const planSubline = subscription
    ? `${formatGbp(subscription.subtotal)} / month${
        renewsAt ? ` · renews ${format(renewsAt, 'MMM d, yyyy')}` : ''
      }`
    : 'Explore Stark Chat with your trial allowance — no credit card required.';

  const usagePeriod = renewsAt
    ? `${format(subMonths(renewsAt, 1), 'MMM d, yyyy')} - ${format(
        renewsAt,
        'MMM d, yyyy'
      )} (current billing period)`
    : 'Trial allowance — yours until used';

  // A paid-for allocation is minted asynchronously by the invoice.paid
  // webhook; until it lands the plan pool sits below the purchased step (or
  // the local subscription is still `incomplete`). The 2.5s poll below picks
  // up the minted credits, at which point the loader disappears.
  const purchasedStepIndex = subscription
    ? stepIndexForSubtotal(subscription.subtotal)
    : null;

  const purchasedStep =
    purchasedStepIndex !== null ? ESSENTIALS_STEPS[purchasedStepIndex] : null;

  const hasPendingCredits =
    !!subscription &&
    (subscription.status === SubscriptionStatus.incomplete ||
      (!!purchasedStep && usage.query.total < purchasedStep.queries));

  useEffect(() => {
    if (!hasPendingCredits) return;

    const interval = setInterval(() => void refresh(), 2500);

    return () => clearInterval(interval);
  }, [hasPendingCredits, refresh]);

  return (
    <div className="p-5 space-y-5 max-w-155">
      {outstandingInvoice && (
        <Alert
          type="serious"
          message="Your last subscription payment failed. Pay your open invoice to keep your plan active."
          buttonText="View invoice"
          onButtonClick={() =>
            void navigate({ to: '/billing', hash: '2' })
          }
        />
      )}
      {subscription && (isPaused || missingCard) && (
        <Alert
          type="warning"
          message={
            missingCard
              ? 'Your subscription has no payment card allocated. Add a card so renewals can be billed.'
              : 'Your subscription is paused. Resume it to keep your plan renewing.'
          }
          buttonText={missingCard ? 'Add card' : 'Resume'}
          onButtonClick={
            missingCard
              ? openAddCard
              : () => confirmAction(SubscriptionAction.resume)
          }
        />
      )}
      {addCardModal}

      <BillingPanel title="Current plan">
        <div className="flex items-center justify-between gap-4 py-2">
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <p className="text-body-medium font-semibold text-white">
                {planName}
              </p>
              <Pill
                size="small"
                outline
                colour={planStatus.colour}
                text={planStatus.text}
              />
            </div>
            <p className="text-sm text-text-low-priority mt-1">
              {planSubline}
            </p>
          </div>
          {!isEnterprise && (
            <div className="flex items-center gap-3 shrink-0">
              {isUpdating ? (
                // A status change (pause/resume/cancel) is in flight — swap
                // the controls for a loader until the refreshed status lands.
                <FullLoader className="size-5" />
              ) : (
                <>
                  <Button
                    button={{
                      text: subscription ? 'Upgrade usage' : 'Choose a plan',
                      size: 'medium',
                      IconEnd: ArrowRightIcon,
                      onClick: onUpgrade,
                    }}
                  />
                  {manageOptions.length > 0 && (
                    <ContextMenu
                      type="tertiary"
                      size="medium"
                      options={manageOptions}
                      onOptionSelect={confirmAction}
                    />
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </BillingPanel>

      <BillingPanel
        title="Monthly usage"
        action={
          hasPendingCredits ? <Loader width={20} height={20} /> : undefined
        }
      >
        <p className="text-sm text-text-low-priority">{usagePeriod}</p>
        <UsageBars usage={usage} usageState={usageState} />
        {noUsage && (
          <div className="border border-red-400 bg-red-1000 rounded-xs p-3 flex items-start gap-3">
            <ExclamationCircleIcon className="size-5 text-red-400 shrink-0 mt-0.5" />
            <span className="text-sm text-white">
              No usage remaining - upgrade your plan to continue using Stark
              Chat.
            </span>
          </div>
        )}
      </BillingPanel>
    </div>
  );
};
